import React from "react";
import { User, Pencil } from "lucide-react";
import { getInitials } from "@/shared/lib/utils";
import { cn } from "@/shared/utils/core";

function AvatarContent({
  isLoadingPhoto,
  profilePictureUrl,
  fullName,
  size,
}: {
  isLoadingPhoto: boolean;
  profilePictureUrl: string | null;
  fullName: string;
  size: number;
}) {
  if (isLoadingPhoto) {
    return <div className="w-full h-full bg-stone-300 animate-pulse rounded-full"></div>;
  } else if (profilePictureUrl) {
    return (
      <img
        src={profilePictureUrl}
        alt={fullName || "Profile"}
        className="w-full h-full object-cover"
      />
    );
  } else if (fullName) {
    return <span>{getInitials({ fullName: fullName })}</span>;
  } else {
    return <User className="text-white" size={Math.round(size * 0.6)} />;
  }
}

interface ContactProfilePictureProps {
  profilePictureUrl: string | null;
  isLoadingPhoto: boolean;
  fullName: string;
  onProfilePictureClick: () => void;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onProfilePictureSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  size?: number;
  containerClassName?: string;
}

const ContactProfilePicture: React.FC<ContactProfilePictureProps> = ({
  profilePictureUrl,
  isLoadingPhoto,
  fullName,
  onProfilePictureClick,
  fileInputRef,
  onProfilePictureSelect,
  size = 80,
  containerClassName,
}) => {
  const isCompact = size <= 56;
  return (
    <div className={cn("flex justify-center mb-6", containerClassName)}>
      <div className="relative">
        <button
          onClick={onProfilePictureClick}
          style={{ width: size, height: size }}
          className={cn(
            "rounded-full bg-brand flex items-center justify-center text-white font-semibold overflow-hidden hover:opacity-90 transition-opacity",
            isCompact ? "text-base" : "text-2xl",
          )}
        >
          <AvatarContent
            isLoadingPhoto={isLoadingPhoto}
            profilePictureUrl={profilePictureUrl}
            fullName={fullName}
            size={size}
          />
        </button>
        <button
          onClick={onProfilePictureClick}
          aria-label="Edit profile picture"
          className={cn(
            "absolute bottom-0 right-0 bg-white rounded-full flex items-center justify-center shadow-md border border-primary hover:opacity-90 transition-opacity cursor-pointer",
            isCompact ? "size-5" : "size-7",
          )}
        >
          <Pencil className={cn("text-gray-600", isCompact ? "size-3" : "size-4")} />
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={onProfilePictureSelect}
        className="hidden"
      />
    </div>
  );
};

export default ContactProfilePicture;
